"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import CardProfilePlayer from "./CardProfilePlayer";
import { TVideo } from "@/actions/types";
import {
  getVideo,
  getVideoFromFollowing,
  resolveV3VideoUrl,
} from "@/actions/videos";
import CardActions from "./CardActions";
import { SupportedLanguage } from "@/lib/shared";

type PageProps = {
  initialVideo: TVideo;
  onScrollUp?: () => void;
  onScrollDown?: () => void;
  onSlideChange?: (index: number) => void;
  isLoggedIn: boolean;
  activeTab: string;
  isFollowingPage?: boolean;
  myId?: string;
  locale: SupportedLanguage;
};

export default function VideoFeed({
  onScrollUp,
  onScrollDown,
  onSlideChange,
  initialVideo,
  isLoggedIn,
  activeTab,
  isFollowingPage,
  myId,
  locale,
}: PageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Visible viewport for one reel (inside padding) — measured for accurate slide height on mobile. */
  const reelViewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  /** Mirrors scrollY for end-of-gesture snap (avoids stale closure on touchend). */
  const scrollYRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    y: 0,
    scrollY: 0,
    timestamp: 0,
  });
  const [velocity, setVelocity] = useState(0);

  const snapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const [itemHeight, setItemHeight] = useState(800);

  const [videos, videosSet] = useState<Record<string, TVideo>>({
    [initialVideo.id]: initialVideo,
  });
  const [videoOrder, videoOrderSet] = useState<string[]>([initialVideo.id]);
  const [activeIndex, activeIndexSet] = useState(0);
  const [isScrollDisabled, isScrollDisabledSet] = useState(false);

  // Resolve initial video v3 URL on mount
  useEffect(() => {
    resolveV3VideoUrl(initialVideo.videoLink).then((resolvedUrl) => {
      if (resolvedUrl !== initialVideo.videoLink) {
        videosSet((prev) => ({
          ...prev,
          [initialVideo.id]: { ...initialVideo, videoLink: resolvedUrl },
        }));
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Video items data - you can make this dynamic
  const videosArray = videoOrder.map((id) => videos[id]).filter(Boolean);
  const maxScroll = (videosArray.length - 1) * itemHeight;

  // Virtual scrolling: calculate which videos to render
  const getVisibleVideoIndices = useCallback(() => {
    const currentSlideIndex = Math.round(scrollY / itemHeight);
    const bufferSize = 1; // Render 1 slide before and after for smooth transitions

    const startIndex = Math.max(0, currentSlideIndex - bufferSize);
    const endIndex = Math.min(
      videosArray.length - 1,
      currentSlideIndex + bufferSize,
    );

    return { startIndex, endIndex, currentSlideIndex };
  }, [scrollY, itemHeight, videosArray.length]);

  const { startIndex, endIndex } = getVisibleVideoIndices();

  // Debug: Log which videos are being rendered
  // useEffect(() => {
  //   console.log(
  //     `Rendering videos ${startIndex} to ${endIndex} (current: ${currentSlideIndex})`
  //   );
  // }, [startIndex, endIndex, currentSlideIndex]);

  /** ~42% drag from one slide toward the next snaps (TikTok-like); momentum adds extra slides on flick. */
  const snapToNearestSlide = useCallback(
    (currentScrollY: number, momentum = 0) => {
      const H = Math.max(itemHeight, 1);
      let targetIndex = Math.floor((currentScrollY + H * 0.42) / H);

      if (Math.abs(momentum) > H * 0.06) {
        const extra = Math.round(momentum / (H * 0.35));
        targetIndex += Math.max(-2, Math.min(2, extra));
      }

      // Clamp to valid range
      targetIndex = Math.max(0, Math.min(videosArray.length - 1, targetIndex));
      const targetScrollY = targetIndex * itemHeight;

      setIsSnapping(true);
      setScrollY(targetScrollY);
      scrollYRef.current = targetScrollY;
      activeIndexSet(targetIndex);
      onSlideChange?.(targetIndex);

      // Dispatch custom event for video index change
      window.dispatchEvent(
        new CustomEvent("videoIndexChange", { detail: { index: targetIndex } }),
      );

      // Reset snapping state after animation
      setTimeout(() => setIsSnapping(false), 500);
    },
    [itemHeight, onSlideChange, videosArray.length],
  );

  const updateScrollPosition = useCallback(
    (newScrollY: number, shouldSnap = false, momentum = 0) => {
      // Simple clamping without aggressive resistance
      let clampedScrollY = Math.max(0, Math.min(maxScroll, newScrollY));

      // Only apply gentle resistance at boundaries
      if (newScrollY < 0) {
        clampedScrollY = newScrollY * 0.3; // Gentle bounce at top
      } else if (newScrollY > maxScroll) {
        const excess = newScrollY - maxScroll;
        clampedScrollY = maxScroll + excess * 0.3; // Gentle bounce at bottom
      }

      setScrollY(clampedScrollY);
      scrollYRef.current = clampedScrollY;

      // Calculate current slide index
      const currentIndex = Math.round(clampedScrollY / Math.max(itemHeight, 1));
      activeIndexSet(currentIndex);
      onSlideChange?.(currentIndex);

      // Dispatch custom event for video index change
      window.dispatchEvent(
        new CustomEvent("videoIndexChange", {
          detail: { index: currentIndex },
        }),
      );

      // Clear existing snap timeout
      if (snapTimeoutRef.current) {
        clearTimeout(snapTimeoutRef.current);
      }

      // Set up snapping after scroll stops (only if not already snapping)
      if (shouldSnap && !isSnapping) {
        snapTimeoutRef.current = setTimeout(() => {
          snapToNearestSlide(clampedScrollY, momentum);
        }, 85);
      }
    },
    [maxScroll, itemHeight, onSlideChange, snapToNearestSlide, isSnapping],
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (isDragging || isScrollDisabled) return;

      e.preventDefault();

      // If currently snapping, interrupt it to allow continuous scrolling
      if (isSnapping) {
        setIsSnapping(false);
      }

      const scaledDelta = e.deltaY * 1.35;
      const newScrollY = scrollY + scaledDelta;

      const momentum = scaledDelta * 0.85;
      updateScrollPosition(newScrollY, true, momentum);

      // Call direction callbacks
      if (e.deltaY > 0) {
        onScrollDown?.();
      } else if (e.deltaY < 0) {
        onScrollUp?.();
      }
    },
    [
      scrollY,
      isDragging,
      isSnapping,
      isScrollDisabled,
      updateScrollPosition,
      onScrollUp,
      onScrollDown,
    ],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isScrollDisabled || isSnapping) return;

      const currentIndex = Math.round(scrollY / itemHeight);

      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (currentIndex <= 0) return;
        snapToNearestSlide((currentIndex - 1) * itemHeight);
        onScrollUp?.();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (currentIndex >= videosArray.length - 1) return;
        snapToNearestSlide((currentIndex + 1) * itemHeight);
        onScrollDown?.();
      }
    },
    [
      scrollY,
      itemHeight,
      snapToNearestSlide,
      onScrollUp,
      onScrollDown,
      isScrollDisabled,
      isSnapping,
      videosArray.length,
    ],
  );

  // Mouse drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isScrollDisabled) return;
      setIsDragging(true);
      setDragStart({ y: e.clientY, scrollY, timestamp: Date.now() });
      setVelocity(0);
    },
    [scrollY, isScrollDisabled],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaY = dragStart.y - e.clientY;
      const currentTime = Date.now();
      const timeDelta = currentTime - dragStart.timestamp;

      // Calculate velocity for momentum
      if (timeDelta > 0) {
        const newVelocity = deltaY / timeDelta;
        setVelocity(newVelocity);
      }

      // Allow multi-slide dragging with increased limits
      const maxDragDistance = itemHeight * 3; // Allow up to 3 slides
      const clampedDeltaY = Math.max(
        -maxDragDistance,
        Math.min(maxDragDistance, deltaY),
      );

      const touchBoost =
        typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches
          ? 1.55
          : 1.15;
      const newScrollY = dragStart.scrollY + clampedDeltaY * touchBoost;
      updateScrollPosition(newScrollY, false); // No snapping during drag
    },
    [isDragging, dragStart, updateScrollPosition, itemHeight],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      const momentum = velocity * 180;
      snapToNearestSlide(scrollYRef.current, momentum);
    }
    setIsDragging(false);
    setVelocity(0);
  }, [isDragging, scrollY, velocity, snapToNearestSlide]);

  // Touch handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isScrollDisabled) return;
      setIsDragging(true);
      setDragStart({ y: e.touches[0].clientY, scrollY, timestamp: Date.now() });
      setVelocity(0);
    },
    [scrollY, isScrollDisabled],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      const deltaY = dragStart.y - e.touches[0].clientY;
      const currentTime = Date.now();
      const timeDelta = currentTime - dragStart.timestamp;

      // Calculate velocity for momentum
      if (timeDelta > 0) {
        const newVelocity = deltaY / timeDelta;
        setVelocity(newVelocity);
      }

      // Allow multi-slide dragging with increased limits
      const maxDragDistance = itemHeight * 3; // Allow up to 3 slides
      const clampedDeltaY = Math.max(
        -maxDragDistance,
        Math.min(maxDragDistance, deltaY),
      );

      const newScrollY = dragStart.scrollY + clampedDeltaY * 1.55;
      updateScrollPosition(newScrollY, false); // No snapping during drag
    },
    [isDragging, dragStart, updateScrollPosition, itemHeight],
  );

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      const momentum = velocity * 180;
      snapToNearestSlide(scrollYRef.current, momentum);
    }
    setIsDragging(false);
    setVelocity(0);
  }, [isDragging, scrollY, velocity, snapToNearestSlide]);

  // One slide height = visible reel viewport (ResizeObserver + orientation; fallback: resize)
  useEffect(() => {
    const el = reelViewportRef.current;
    if (!el) return;

    const updateItemHeight = () => {
      const h = el.clientHeight;
      if (h > 0) setItemHeight(h);
    };

    updateItemHeight();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => updateItemHeight());
      ro.observe(el);
      window.addEventListener("orientationchange", updateItemHeight);
      return () => {
        ro.disconnect();
        window.removeEventListener("orientationchange", updateItemHeight);
      };
    }

    window.addEventListener("resize", updateItemHeight);
    return () => window.removeEventListener("resize", updateItemHeight);
  }, []);

  useEffect(() => {
    scrollYRef.current = scrollY;
  }, [scrollY]);

  useEffect(() => {
    // Wheel events - listen on document for page-wide scrolling
    document.addEventListener("wheel", handleWheel, { passive: false });

    // Keyboard events
    document.addEventListener("keydown", handleKeyDown);

    // Mouse events for drag
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    // Touch events for mobile drag
    if (isDragging) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    handleWheel,
    handleKeyDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
    isDragging,
  ]);

  const updateVideo = useCallback(
    (videoId: string, updates: Partial<TVideo>) => {
      videosSet((prev) => {
        if (!prev[videoId]) return prev;
        return {
          ...prev,
          [videoId]: {
            ...prev[videoId],
            ...updates,
          },
        };
      });
    },
    [],
  );

  const isLoadingMoreRef = useRef(false);

  const loadMoreVideos = useCallback(async () => {
    if (isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    try {
      const getActionVideo = isFollowingPage ? getVideoFromFollowing : getVideo;
      const res = await getActionVideo({ filter: activeTab });
      if (res.status === "success" && res.metaData) {
        const video = res.metaData;
        const resolvedUrl = await resolveV3VideoUrl(video.videoLink);
        const resolved = { ...video, videoLink: resolvedUrl };
        videosSet((prev) => ({
          ...prev,
          [resolved.id]: resolved,
        }));
        videoOrderSet((prev) => [...prev, resolved.id]);
      }
    } catch (error) {
      console.error("Failed to load more videos:", error);
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [activeTab, isFollowingPage]);

  // Prefetch the next video after idle so we don't compete with hydration + resolving
  // the first video URL (server already returned the first clip).
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) void loadMoreVideos();
    };
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      timeoutId = setTimeout(run, 300);
    }

    return () => {
      cancelled = true;
      if (
        idleId !== undefined &&
        typeof window !== "undefined" &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [loadMoreVideos]);

  // Detect when a dialog is open and disable scrolling
  useEffect(() => {
    const checkDialogPresence = () => {
      const dialog = document.querySelector('div[role="dialog"]');
      isScrollDisabledSet(!!dialog);
    };

    // Check initially
    checkDialogPresence();

    // Set up a MutationObserver to watch for dialog changes
    const observer = new MutationObserver(checkDialogPresence);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Load more videos when reaching the last slide
  useEffect(() => {
    const currentSlideIndex = Math.round(scrollY / itemHeight);
    const isAtLastSlide = currentSlideIndex >= videosArray.length - 1;

    if (isAtLastSlide && videosArray.length > 1) {
      loadMoreVideos();
    }
  }, [scrollY, itemHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative h-full" ref={containerRef}>
      <div className="h-full min-h-0 max-lg:pt-0 lg:pt-4">
        <div className="h-full min-h-0 overflow-hidden max-lg:rounded-none lg:rounded-t-2xl">
          <div
            ref={reelViewportRef}
            className="h-full w-full min-h-0 overflow-hidden relative"
          >
            <div
              className="h-full w-full min-h-0 overflow-hidden touch-none focus:outline-none cursor-grab active:cursor-grabbing"
              tabIndex={0}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div
                ref={contentRef}
                className={cn(
                  "w-full relative",
                  isSnapping
                    ? "transition-transform duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]"
                    : "transition-transform duration-75 ease-out",
                )}
                style={{
                  transform: `translateY(-${scrollY}px)`,
                  height: `${videosArray.length * itemHeight}px`,
                }}
              >
                {/* Render only visible videos with virtual positioning */}
                {Array.from({ length: endIndex - startIndex + 1 }, (_, i) => {
                  const index = startIndex + i;
                  const item = videosArray[index];

                  if (!item) return null;

                  return (
              <div
                key={`${item.id}-${index}`}
                className="w-full absolute top-0 start-0 max-lg:pb-0"
                style={{
                  height: `${itemHeight}px`,
                  transform: `translateY(${index * itemHeight}px)`,
                }}
              >
                      <CardProfilePlayer
                        locale={locale}
                        isFollowed={item.isFollowed}
                        tags={item.tags}
                        contentURL={item.videoLink}
                        name={item.videoOwnerName}
                        position={item.position}
                        age={item.videoOwnerAge}
                        years={item.experience}
                        views={item.noOfViews}
                        avatarURL={item.profileImage}
                        availability="Available"
                        description={item.description}
                        isActive={index === activeIndex}
                        userId={item.userId}
                        onScrollDisableChange={isScrollDisabledSet}
                        isLoggedIn={isLoggedIn}
                        myId={myId}
                        hasJobAd={item.hasJobAd}
                        jobId={item.jobId}
                        isApplied={item.isApplied}
                      />
                      {/* موبايل فقط: أزرار ضمن الريل تتحرك مع التمرير (يُخفى من lg فما فوق) */}
                      <CardActions
                        className="lg:hidden"
                        variant="reelRail"
                        isActive={index === activeIndex}
                        videoId={item.id}
                        userId={item.userId}
                        videoURL={item.videoLink}
                        likes={item.noOfLikes}
                        comments={item.noOfComments}
                        shares={item.noOfShares}
                        supportComments={0}
                        save={item.noOfSaves}
                        isLiked={item.isLiked}
                        isSaved={item.isSaved}
                        isLoggedIn={isLoggedIn}
                        locale={locale}
                        onVideoUpdate={updateVideo}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* شاشات lg فما فقط: عمود الإجراءات ثابت بجانب الفيديو (لا يتكرر مع كل ريل) */}
      {videosArray[activeIndex] && (
        <div className="hidden lg:block">
          <CardActions
            variant="fixedColumn"
            videoId={videosArray[activeIndex].id}
            userId={videosArray[activeIndex].userId}
            videoURL={videosArray[activeIndex].videoLink}
            likes={videosArray[activeIndex].noOfLikes}
            comments={videosArray[activeIndex].noOfComments}
            shares={videosArray[activeIndex].noOfShares}
            supportComments={0}
            save={videosArray[activeIndex].noOfSaves}
            isLiked={videosArray[activeIndex].isLiked}
            isSaved={videosArray[activeIndex].isSaved}
            isLoggedIn={isLoggedIn}
            locale={locale}
            onVideoUpdate={updateVideo}
          />
        </div>
      )}
    </div>
  );
}
